/**
 * feedHashtag.js — pure helpers for the /feed?hashtag= view (RW-2961).
 *
 * The hashtag view used to filter only the posts the feed already had in
 * memory (App.js's live window of the newest 50, plus older pages scrolled
 * in), so a #tag link on an April post opened "#uptown · 0 posts". The view
 * now reads its own pages from the server (feedPostRepository
 * .listPostsByHashtag, useHashtagPosts) and those posts go through the SAME
 * client filter pipeline as the normal feed (deleted, group membership,
 * office, date, pills) — this module only merges them and words the count.
 */

// One server page. Rule 25: at most 20 document reads per page.
export const HASHTAG_PAGE_SIZE = 20;

function millis(post) {
  const ts = post && post.timestamp;
  // A post this client just wrote has a pending serverTimestamp (null) — it is
  // the newest thing on screen.
  if (ts == null) return Number.POSITIVE_INFINITY;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  if (ts instanceof Date) return ts.getTime();
  return 0;
}

// A scheduled post waits in `posts` with status 'scheduled' until
// publishScheduledPosts flips it; a hashtag view never shows one early.
export function isPublishedPost(post) {
  return !!post && post.status !== 'scheduled';
}

/**
 * The hashtag view's source list: posts already loaded (live window + older
 * pages) that carry the tag, plus the server's hashtag pages. The loaded copy
 * wins on a duplicate id — it is the live one (reactions, comments, a delete).
 * Newest first, scheduled posts left out.
 */
export function mergeHashtagPosts(loadedPosts, serverPosts, tag) {
  const out = [];
  const seen = new Set();
  const add = (p) => {
    if (!p || !p.id || seen.has(p.id)) return;
    seen.add(p.id);
    if (isPublishedPost(p)) out.push(p);
  };
  (Array.isArray(loadedPosts) ? loadedPosts : [])
    .filter((p) => p && Array.isArray(p.hashtags) && p.hashtags.includes(tag))
    .forEach(add);
  (Array.isArray(serverPosts) ? serverPosts : []).forEach(add);
  return out.sort((a, b) => millis(b) - millis(a));
}

/**
 * The banner's count. `count` is what the view can show right now; `hasMore`
 * says older tagged posts exist that are not loaded yet ("20+ posts"), and
 * `recentOnly` is the fallback when the server read failed — then only the
 * recent posts were searched, and the banner says so instead of a flat "0".
 */
export function hashtagCountLabel({ count = 0, hasMore = false, loading = false, recentOnly = false } = {}) {
  // Nothing to show yet but more pages to read: the sentinel is already
  // reading the next one (a page can be all hidden posts).
  if ((loading || hasMore) && count === 0 && !recentOnly) return 'Loading posts…';
  const plural = (n) => (n === 1 ? '' : 's');
  if (recentOnly) return `${count} recent post${plural(count)}`;
  if (hasMore) return `${count}+ posts`;
  return `${count} post${plural(count)}`;
}
